import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';

/**
 * SimilarUrlsPanel - When user clicks "Find Similar" on a URL,
 * this panel shows LLM-powered semantically similar URLs.
 */
const SimilarUrlsPanel = ({ resultId, resultTitle, onClose }) => {
  const { token } = useAuth();
  const { get } = useXHR(token);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState('');
  const [fetched, setFetched] = useState(false);

  const fetchSimilar = useCallback(() => {
    if (!resultId) return;
    setLoading(true);
    get(`/similar-urls/find/${resultId}?limit=10`, {
      onSuccess: (data) => {
        setSimilar(data.similar || []);
        setMethod(data.method || '');
      },
      onFinally: () => { setLoading(false); setFetched(true); }
    });
  }, [get, resultId]);

  // Auto-fetch on mount
  React.useEffect(() => { if (!fetched) fetchSimilar(); }, [fetchSimilar, fetched]);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, width: 420, height: '100vh', zIndex: 1000,
      background: 'linear-gradient(135deg, rgba(253, 251, 247,0.98), rgba(20,15,45,0.98))',
      borderLeft: '2px solid rgba(253, 224, 71,0.3)', overflowY: 'auto',
      boxShadow: '-8px 0 30px rgba(0,0,0,0.5)',
      animation: 'slideInRight 0.2s ease-out'
    }} data-testid="similar-urls-panel">
      {/* Header */}
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid rgba(253, 224, 71,0.2)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'rgba(253, 224, 71,0.08)'
      }}>
        <div>
          <h3 style={{ margin: 0, color: '#c4b5fd', fontSize: '0.9rem' }}>
            <i className="fas fa-project-diagram" style={{ marginRight: 8 }} />Similar URLs
          </h3>
          <p style={{ margin: '4px 0 0', color: '#71717a', fontSize: '0.68rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Finding content similar to: {resultTitle}
          </p>
        </div>
        <button onClick={onClose} data-testid="close-similar-panel"
          style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700 }}>
          X
        </button>
      </div>

      {/* Method badge */}
      {method && (
        <div style={{ padding: '6px 18px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
          <span style={{
            background: method === 'ai' ? 'rgba(253, 224, 71,0.15)' : 'rgba(245,158,11,0.15)',
            color: method === 'ai' ? '#047857' : '#f59e0b',
            padding: '2px 8px', borderRadius: 6, fontSize: '0.6rem', fontWeight: 700
          }}>
            {method === 'ai' ? 'AI-Powered (GPT)' : 'Keyword Match'}
          </span>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '12px 18px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#71717a' }}>
            <i className="fas fa-brain fa-spin" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block', color: '#FDE047' }} />
            <p style={{ fontSize: '0.8rem' }}>Analyzing semantic similarity...</p>
          </div>
        ) : similar.length === 0 && fetched ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>
            <i className="fas fa-search" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block', opacity: 0.4 }} />
            <p style={{ fontSize: '0.8rem' }}>No similar URLs found in your database.</p>
            <p style={{ fontSize: '0.65rem', color: '#444' }}>Try adding more URLs through searches to build your knowledge base.</p>
          </div>
        ) : (
          similar.map((url, i) => (
            <div key={url.id || i} style={{
              padding: '10px 12px', marginBottom: 8, borderRadius: 10,
              background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
              transition: 'background 0.15s'
            }} data-testid={`similar-url-${i}`}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(253, 224, 71,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)'}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ color: '#555', fontSize: '0.65rem', minWidth: 18, paddingTop: 2, textAlign: 'right' }}>{i + 1}.</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={url.url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#93c5fd', fontSize: '0.78rem', fontWeight: 500, textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {url.title || 'Untitled'}
                  </a>
                  {url.snippet && (
                    <p style={{ color: '#71717a', fontSize: '0.65rem', margin: '3px 0 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                      {url.snippet}
                    </p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                    {url.article_type && (
                      <span style={{ background: 'rgba(253, 224, 71,0.12)', color: '#047857', padding: '1px 5px', borderRadius: 5, fontSize: '0.55rem' }}>{url.article_type}</span>
                    )}
                    {url.categories?.slice(0, 3).map((cat, ci) => (
                      <span key={ci} style={{ background: 'rgba(253, 230, 138,0.1)', color: '#e879a0', padding: '1px 5px', borderRadius: 5, fontSize: '0.55rem' }}>{cat}</span>
                    ))}
                    {url.root_domain && (
                      <span style={{ color: '#555', fontSize: '0.5rem' }}>{url.root_domain}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SimilarUrlsPanel;
